import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '../../stores/authStore';

export default function ProtectedRoute({ children, guest }: { children: ReactNode; guest?: ReactNode }) {
  const { user, loading } = useAuthStore();
  const hydrate = useAuthStore((s) => s._hydrate);
  const [hardTimeout, setHardTimeout] = useState(false);
  const hydrated = useRef(false);

  // 初始化：从 localStorage 恢复认证状态
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      hydrate();
    }
  }, [hydrate]);

  // 硬超时兜底：6 秒后如果还在 loading，直接跳到登录页
  useEffect(() => {
    const t = setTimeout(() => setHardTimeout(true), 6000);
    return () => clearTimeout(t);
  }, []);

  // 给了 guest 落地的路由（只有站点根）：确认未登录后就地渲染登录页，不再跳转改地址。
  // 判据必须带 loading：只看 user 会在「localStorage 已清但 HttpOnly 续期 Cookie 还在」时
  // 先闪一下登录页再切进工作台（Cookie 按 host 不按 port，另起端口就会踩到这个态）。
  // 等到 hydrate 出结论再落地，已登录用户走的仍是下面原来的转圈 → 工作台，一格像素都不闪。
  if (guest !== undefined && !loading && !user) return <>{guest}</>;

  if (!loading && !user) return <Navigate to="/login" replace />;
  if (hardTimeout && loading) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400">正在验证身份...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
